import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../landingPage/Sidebar';
import { PageHeader } from '../../components/ui/header';
import { PrimaryTabs } from '../../components/ui/tabs';
import { FilterButtons } from '../../components/ui/filter';
import connectionIcon from '../../Images/connecttion.png';
import myConnIcon from '../../Images/my_conn.png';
import salesforceCard from '../../Images/salesforce_card.png';
import oracleCard from '../../Images/oracle_card.png';
import hubspotCard from '../../Images/hubspot_card.png';
import sqlCard from '../../Images/sql_card.png';
import jiraCard from '../../Images/jira_card.png';

const CONNECTOR_DATA = [
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Salesforce is a cloud-based CRM that unifies sales, marketing, and service, using AI to improve customer relationships and productivity.',
    category: 'Saas',
    icon: salesforceCard,
  },
  {
    id: 'oracle',
    name: 'Oracle',
    description: 'Oracle is a powerful relational database system that securely stores, manages, and processes large volumes of data with high performance and reliability.',
    category: 'Database',
    icon: oracleCard,
  },
  {
    id: 'hubspot',
    name: 'Hubspot',
    description: 'HubSpot is a CRM platform that helps businesses manage marketing, sales, customer service by organizing customer data, automating workflows to drive growth.',
    category: 'Saas',
    icon: hubspotCard,
  },
  {
    id: 'sql-server',
    name: 'SQL Server',
    description: 'SQL Server is a powerful relational database system that securely stores, manages, and processes data with high performance, scalability, and reliability.',
    category: 'Database',
    icon: sqlCard,
  },
  {
    id: 'jira',
    name: 'Jira',
    description: 'Jira is a project management and issue-tracking tool that helps teams plan, track, and manage work with real-time visibility.',
    category: 'Saas',
    icon: jiraCard,
  },
];

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'saas', label: 'Saas' },
  { value: 'database', label: 'Database' },
  { value: 'streaming', label: 'Streaming' },
];

const ConnectorMarketplace = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(1);
  const [selectedFilter, setSelectedFilter] = useState('all');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'connectors') {
      setActiveTab(1);
    } else {
      setActiveTab(1);
    }
  }, [location.search]);

  const handleTabChange = (newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      navigate('/myconnections');
    } else {
      navigate('/connector-marketplace?tab=connectors');
    }
  };

  const handleFilterChange = (filterValue) => {
    setSelectedFilter(filterValue);
  };

  const filteredConnectors = selectedFilter === 'all'
    ? CONNECTOR_DATA
    : CONNECTOR_DATA.filter(connector => 
        connector.category.toLowerCase() === selectedFilter.toLowerCase()
      );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#FAFBFD' }}>
      <Sidebar />
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <PageHeader
          title="Connector Marketplace"
          createButtonText="Request Connector"
          createButtonClick={() => {
            console.log('Request Connector clicked');
          }}
          showConnectorIcon={false}
          showSearchIcon={true}
          searchIconClick={() => {
            console.log('Search clicked');
          }}
        />

        <Box sx={{ flexGrow: 1, px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1.5, sm: 2 } }}>
          <PrimaryTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            tabs={[
              {
                label: 'My Connections',
                icon: myConnIcon,
                route: '/myconnections',
              },
              {
                label: 'Connectors',
                icon: connectionIcon,
                route: '/connector-marketplace?tab=connectors',
              },
            ]}
          />

          {activeTab === 1 && (
            <Box>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2.5, md: 3, lg: 4 },
                  mb: { xs: 2, sm: 2.5, md: 3 },
                  borderRadius: '4px',
                  bgcolor: '#ECFBF6',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, sm: 2.5, md: 3 }, alignItems: { md: 'flex-start' } }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: 'IBM Plex Sans, sans-serif',
                        fontWeight: 600,
                        fontStyle: 'normal',
                        fontSize: { xs: '1.125rem', sm: '1.1875rem', md: '1.25rem' },
                        lineHeight: '100%',
                        letterSpacing: '0%',
                        verticalAlign: 'middle',
                        color: '#3F3F3F',
                        mb: { xs: 0.75, sm: 1 },
                      }}
                    >
                      Connector Library
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontStyle: 'normal',
                        fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                        lineHeight: 1.4,
                        letterSpacing: '0%',
                        color: '#3F3F3F',
                      }}
                    >
                      Browse and discover available source and destination connectors to start building your data pipelines.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: { xs: '100%', sm: '100%', md: 450 },
                      height: { xs: '90px', sm: '100px', md: '110px' },
                      borderRadius: '4px',
                      overflow: 'hidden',
                      mt: { xs: 1, md: 0 },
                    }}
                  >
                    <Box
                      component="img"
                      src=""
                      alt="Connector Library Diagram"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                </Box>
              </Paper>

              <FilterButtons
                options={FILTER_OPTIONS}
                selectedValue={selectedFilter}
                onChange={handleFilterChange}
              />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                  },
                  gap: { xs: 2, sm: 2.5, md: 3 },
                }}
              >
                {filteredConnectors.map((connector) => (
                  <Paper
                    key={connector.id}
                    elevation={0}
                    sx={{
                      p: { xs: 1.5, sm: 2, md: 2.5 },
                      borderRadius: '4px',
                      background: 'linear-gradient(0deg, #EEF3FF, #EEF3FF)',
                      display: 'flex',
                      flexDirection: 'row',
                      gap: { xs: 1.25, sm: 1.5, md: 2 },
                      alignItems: 'flex-start',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: '48px', sm: '56px', md: '64px' },
                        height: { xs: '48px', sm: '56px', md: '64px' },
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        bgcolor: '#FFFFFF',
                        overflow: 'hidden',
                      }}
                    >
                      {connector.icon && (
                        <Box
                          component="img"
                          src={connector.icon}
                          alt={connector.name}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: { xs: 0.75, sm: 1 } }}>
                      <Typography
                        sx={{
                          fontFamily: 'IBM Plex Sans, sans-serif',
                          fontWeight: 500,
                          fontStyle: 'normal',
                          fontSize: { xs: '1rem', sm: '1.0625rem', md: '1.125rem' },
                          lineHeight: '100%',
                          letterSpacing: '0%',
                          color: '#000000',
                        }}
                      >
                        {connector.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: 'IBM Plex Sans, sans-serif',
                          fontWeight: 400,
                          fontStyle: 'normal',
                          fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                          lineHeight: 1.4,
                          letterSpacing: '0%',
                          color: '#525252',
                        }}
                      >
                        {connector.description}
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ConnectorMarketplace;
